import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

// BreadcrumbList JSON-LD for every page, built from the URL: Home > [section] > page.
const SITE = 'https://www.getfriday.com';

const PERSONAL = { name: 'Personal Services', path: '/personal/virtual-personal-assistant' };
const BUSINESS = { name: 'Business Services', path: '/business/services' };

// Short URLs that serve the same page; their schema uses the full page URL.
const ALIASES = {
  '/personal': '/personal/virtual-personal-assistant',
  '/business': '/business/services',
};
const INDUSTRIES = { name: 'Industries', path: '/industries' };
const BOOKKEEPING = { name: 'Bookkeeping Services', path: '/business/bookkeeping-service' };

// path -> [page name, ...parent crumbs]
const PAGES = {
  '/about-us': ['About Us'],
  '/contact-us': ['Contact Us'],
  '/how-we-work': ['How We Work'],
  '/how-to-delegate': ['How to Delegate'],
  '/why-getfriday': ['Why GetFriday'],
  '/faqs': ['FAQs'],
  '/refer-a-friend': ['Refer a Friend'],
  '/gift-of-time': ['Gift of Time'],
  '/testimonials': ['Testimonials'],
  '/press-and-media': ['Press and Media'],
  '/videos-and-podcasts': ['Videos and Podcasts'],
  '/4-hour-workweek-ymii': ['4-Hour Workweek'],
  '/privacy-policy': ['Privacy Policy'],
  '/terms-and-conditions': ['Terms and Conditions'],
  '/refund-policy': ['Refund Policy'],
  '/disclaimer': ['Disclaimer'],
  '/copyright': ['Copyright'],
  '/sitemap': ['Sitemap'],

  '/personal/virtual-personal-assistant': ['Personal Services'],
  '/personal/family-and-personal': ['Family & Personal', PERSONAL],
  '/personal/travel-management': ['Travel Management', PERSONAL],
  '/personal/online-research': ['Online Research', PERSONAL],
  '/personal/remote-executive-secretary': ['Remote Personal Assistant', PERSONAL],
  '/personal/sundry-tasks': ['Sundry Tasks', PERSONAL],
  '/personal/ai-guidance': ['AI For Personal Life', PERSONAL],
  '/personal/pricing-plans': ['Pricing Plans', PERSONAL],

  '/business/services': ['Business Services'],
  '/business/business-process-support': ['Business Process Support', BUSINESS],
  '/business/website-services': ['Website & App Development', BUSINESS],
  '/business/digital-marketing': ['SEO / Digital Marketing', BUSINESS],
  '/business/bookkeeping-service': ['Bookkeeping Services', BUSINESS],
  '/business/bookkeeping-service/quickbooks-bookkeeping': ['QuickBooks Bookkeeping', BUSINESS, BOOKKEEPING],
  '/business/research-analytics': ['Research & Analytics', BUSINESS],
  '/business/odoo-implementation': ['Odoo Implementation', BUSINESS],
  '/business/ai-automation': ['AI Automation & Integration', BUSINESS],
  '/business/remote-executive-secretary': ['Remote Executive Secretary', BUSINESS],
  '/business/vacation-rental': ['Vacation Rental', BUSINESS],
  '/business/quote': ['Get a Quote', BUSINESS],

  '/industries': ['Industries'],
  '/industries/accounting-bookkeeping': ['Accounting & Bookkeeping', INDUSTRIES],
  '/industries/coaching-training': ['Coaching & Training', INDUSTRIES],
  '/industries/consulting': ['Consulting', INDUSTRIES],
  '/industries/digital-marketing-agencies': ['Digital Marketing Agencies', INDUSTRIES],
  '/industries/ecommerce': ['Ecommerce', INDUSTRIES],
  '/industries/education-edtech': ['Education & EdTech', INDUSTRIES],
  '/industries/financial-advisory': ['Financial Advisory', INDUSTRIES],
  '/industries/healthcare': ['Healthcare', INDUSTRIES],
  '/industries/hospitality': ['Hospitality', INDUSTRIES],
  '/industries/legal-services': ['Legal Services', INDUSTRIES],
  '/industries/nonprofit-organizations': ['Nonprofit Organizations', INDUSTRIES],
  '/industries/podcasting-content-creation': ['Podcasting & Content Creation', INDUSTRIES],
  '/industries/property-management': ['Property Management', INDUSTRIES],
  '/industries/real-estate': ['Real Estate', INDUSTRIES],
  '/industries/recruitment-staffing': ['Recruitment & Staffing', INDUSTRIES],
  '/industries/small-business': ['Small Business', INDUSTRIES],
  '/industries/startups-entrepreneurs': ['Startups & Entrepreneurs', INDUSTRIES],
  '/industries/vacation-rentals': ['Vacation Rentals', INDUSTRIES],
};

export const breadcrumbJsonLd = (pathname) => {
  const normalized = (pathname.replace(/\/+$/, '') || '/').toLowerCase();
  const path = ALIASES[normalized] || normalized;
  const entry = PAGES[path];
  if (!entry) return null;
  const [name, ...parents] = entry;
  const crumbs = [{ name: 'Home', path: '/' }, ...parents, { name, path }];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": crumb.name,
      "item": SITE + crumb.path,
    })),
  };
};

const BreadcrumbSchema = () => {
  const { pathname } = useLocation();
  const jsonLd = breadcrumbJsonLd(pathname);
  if (!jsonLd) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default BreadcrumbSchema;
